/* eslint-disable max-len */
import { useState } from "react";
import { ImageField, ImageInput, useTranslate } from "react-admin";
import { muiColors } from "themes/colors";
import { EmptyCompanyIcon, TrashIcon } from "assets/Icons";
import { Box, IconButton } from "@mui/material";
import ConfirmationModal from "./ConfirmationModal";

interface CustomImageInputSmallProps {
  source: string;
  label?: string;
  width?: string;
  height?: string;
  borderRadius?: string;
  placeholder?: string | JSX.Element;
  buttonText?: string;
  bg?: string;
}

const CustomImageInputSmall = ({
  source,
  label = " ",
  width = "40px",
  height = "40px",
  borderRadius = "6px",
  placeholder = <EmptyCompanyIcon />,
  buttonText,
  bg = "#eeeae4"
}: CustomImageInputSmallProps) => {
  const [error, setError] = useState("");
  const translate = useTranslate();
  return (
    <Box
      sx={{
        ":not(.previews .RaImageField-image)": {
          ".RaFileInput-removeButton button": {
            display: "none"
          }
        },
        ":has(.previews .RaImageField-image)": {
          ".CustomRemoveIcon": {
            opacity: 1
          },
          ".RaFileInput-removeButton button": {
            display: "flex"
          }
        },
        ".fetchedImageByEdit img": {
          position: "absolute",
          top: "0px",
          left: "0px",
          zIndex: 1,
          objectFit: "cover !important",
          width: `${width} !important`,
          height: `${height} !important`,
          margin: "3px 0px 0px 0px !important",
          borderRadius: borderRadius
        }
      }}
      style={{
        // width: width,
        // minWidth: "250px",
        display: "flex",
        gap: "8px",
        alignItems: "center",
        position: "relative",
        marginBottom: "16px"
      }}
    >
      <ImageInput
        label={label}
        source={source}
        accept="image/*"
        placeholder={placeholder}
        maxSize={10000000}
        options={{
          onDropRejected(fileRejections) {
            if (fileRejections?.[0]?.errors?.[0]?.code === "file-too-large") {
              setError(
                "The file you attempted to upload exceeds our 10MB limit. Please choose a file that is smaller in size."
              );
            } else {
              setError("Invalid file. Please choose other file.");
            }
          }
        }}
        sx={{
          // "& .RaLabeled-label > span": {
          //   position: "sticky",
          //   top: "0px",
          //   left: "0px"
          // },
          // marginBottom: "0px",
          "& .RaFileInput-dropZone": {
            height: height,
            width: width,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: "solid 1px #F2EFEA",
            backgroundColor: bg,
            borderRadius: borderRadius,
            fontSize: "12px",
            color: muiColors.text.disabled
          },
          "& .RaFileInput-removeButton": {
            position: "absolute",
            top: "0px",
            right: "0px",
            width: "100%",
            zIndex: 2,
            button: {
              opacity: 1,
              top: "4px",
              right: "-1px",
              zIndex: 1,
              svg: {
                opacity: 0
              }
            }
          },
          "& .RaImageField-image": {
            objectFit: "cover !important",
            width: `${width} !important`,
            height: `${height} !important`,
            margin: "3px 0px 0px 0px !important",
            borderRadius: borderRadius
          },
          "& .MuiFormHelperText-root": {
            display: "none"
          }
        }}
      >
        <ImageField source="src" />
      </ImageInput>
      <ImageField source={source} className="fetchedImageByEdit" />
      <div className="flex items-center gap-[8px]">
        <Box
          component="label"
          htmlFor={source}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "36px",
            fontSize: "14px",
            fontFamily: "Inter",
            lineHeight: "16px",
            fontWeight: 500,
            borderRadius: "32px",
            textTransform: "none",
            whiteSpace: "nowrap",
            backgroundColor: muiColors.button.paper,
            color: muiColors.text.primary,
            border: `solid 1px ${muiColors.button.border}`,
            padding: "0px 16px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: muiColors.button.secondaryHover
            }
          }}
        >
          {buttonText || translate("common.set_logo")}
        </Box>
        <IconButton
          className="CustomRemoveIcon"
          sx={{ opacity: 0, pointerEvents: "none" }}
        >
          <TrashIcon />
        </IconButton>
      </div>
      {error && (
        <ConfirmationModal
          open={Boolean(error)}
          handleConfirm={() => setError("")}
          onClose={() => setError("")}
          text={error}
          buttonText="Got it"
          title="Warning"
          buttonBg={muiColors.button.primary}
          centerAlign
        />
      )}
    </Box>
  );
};

export default CustomImageInputSmall;
